import { useEffect, useRef, useCallback } from 'react';
import { Capacitor } from '@capacitor/core';
import { getEvents } from '../services/calendar';
import { getTasks } from '../services/task';
import { pushWidgetData } from '../services/widgetBridge';

const WIDGET_SYNC_INTERVAL = 15 * 60 * 1000; // 15 minutes (matches WidgetKit minimum)

/**
 * Hook that periodically pushes today's calendar events and tasks
 * to native iOS shared storage for the widget extension.
 *
 * Only runs on iOS native platform. No-op on web.
 */
export const useWidgetData = () => {
  const isIOS = Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios';
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isSyncingRef = useRef(false);

  const syncWidgetData = useCallback(async () => {
    if (!isIOS || isSyncingRef.current) return;

    isSyncingRef.current = true;
    try {
      // Get today's date range in ISO format
      const now = new Date();
      const startOfDay = new Date(now);
      startOfDay.setHours(0, 0, 0, 0);
      const endOfDay = new Date(now);
      endOfDay.setHours(23, 59, 59, 999);

      // Fetch today's events and active tasks in parallel
      const [eventsResponse, tasks] = await Promise.all([
        getEvents(startOfDay.toISOString(), endOfDay.toISOString()),
        getTasks({ status: 'todo' }),
      ]);

      // Push to native widget storage
      await pushWidgetData(eventsResponse.events, tasks);
    } catch (error) {
      // Silently fail — widget data is best-effort
      console.warn('[useWidgetData] Sync failed:', error);
    } finally {
      isSyncingRef.current = false;
    }
  }, [isIOS]);

  useEffect(() => {
    if (!isIOS) return;

    // Initial sync after a short delay (let the app finish loading)
    const initialTimer = setTimeout(() => {
      syncWidgetData();
    }, 3000);

    // Periodic sync
    intervalRef.current = setInterval(syncWidgetData, WIDGET_SYNC_INTERVAL);

    return () => {
      clearTimeout(initialTimer);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isIOS, syncWidgetData]);

  return { syncWidgetData };
};

